"use client";

import "swiper/css";

import { useRef, useState } from "react";

import type { Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import {
  ProductTile,
  type ProductTileProduct,
} from "@/components/productTile/productTile.component";
import { ProductTileQuantityStepper } from "@/components/productTile/productTileQuantityStepper.component";

import type { SuggestedForYouFormAction } from "./suggestedForYou.component";
import { SuggestedForYouControls } from "./suggestedForYouControls.component";
import { SuggestedForYouHeader } from "./suggestedForYouHeader.component";

const SWIPER_BREAKPOINTS = {
  320: { slidesPerView: 1.2, spaceBetween: 16 },
  1024: { slidesPerView: 4, spaceBetween: 20 },
} as const;

export const SuggestedForYouItems = ({
  products,
  basketItems,
  formAction,
  title,
}: SuggestedForYouItemsProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalPages, setTotalPages] = useState(products.length);
  const swiperRef = useRef<SwiperType | null>(null);

  if (!products.length) return null;

  const lineItemQuantities = basketItems ?? {};

  const updatePages = (swiper: SwiperType) => {
    setTotalPages(swiper.snapGrid.length);
  };

  return (
    <section data-testid="suggested-for-you" className="w-full py-10">
      <div className="container mx-auto px-4">
        <SuggestedForYouHeader
          swiperRef={swiperRef}
          title={title}
          totalPages={totalPages}
        />
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            updatePages(swiper);
          }}
          onSlideChange={(swiper) => {
            setActiveIndex(swiper.realIndex);
            updatePages(swiper);
          }}
          onResize={updatePages}
          allowTouchMove
          grabCursor
          spaceBetween={20}
          breakpoints={SWIPER_BREAKPOINTS}
          className="w-full! overflow-hidden!">
          {products.map((product) => (
            <SwiperSlide key={product.slug} className="h-auto p-0.5">
              <input
                id="product-id"
                type="hidden"
                name="productId"
                defaultValue={product.id}
              />
              <ProductTile
                product={product}
                viewDetails
                hideDescriptionOnMobile>
                {formAction ? (
                  <div className="flex py-4">
                    <ProductTileQuantityStepper
                      basketItems={lineItemQuantities}
                      id={product.id}
                      formAction={formAction}
                    />
                  </div>
                ) : null}
              </ProductTile>
            </SwiperSlide>
          ))}
        </Swiper>
        <SuggestedForYouControls
          swiperRef={swiperRef}
          activeIndex={activeIndex}
          totalSlides={totalPages}
        />
      </div>
    </section>
  );
};

export type SuggestedProduct = ProductTileProduct & {
  id: string;
};

export type SuggestedForYouItemsProps = {
  products: SuggestedProduct[];
  basketItems?: Record<string, number>;
  kitId?: string;
  title?: string;
  formAction?: SuggestedForYouFormAction;
};
